import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, TrendingUp, Package, DollarSign, Route, Users, Truck, Loader2, Calendar } from 'lucide-react';

export default function AdminAnalytics() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const fetchData = (params = {}) => {
    setLoading(true);
    api.get('/analytics/full', { params })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const applyRange = () => {
    const params = {};
    if (dateRange.start) params.start = new Date(dateRange.start).toISOString();
    if (dateRange.end) params.end = new Date(dateRange.end + 'T23:59:59').toISOString();
    fetchData(params);
  };

  const setQuickRange = (type) => {
    const now = new Date();
    let s, e = now.toISOString().slice(0, 10);
    if (type === 'all') { setDateRange({ start: '', end: '' }); fetchData(); return; }
    if (type === 'mtd') s = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    else if (type === 'last_month') {
      const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      s = pm.toISOString().slice(0, 10);
      e = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
    } else if (type === 'last_7') s = new Date(now - 7 * 864e5).toISOString().slice(0, 10);
    setDateRange({ start: s, end: e });
    const params = { start: new Date(s).toISOString(), end: new Date(e + 'T23:59:59').toISOString() };
    fetchData(params);
  };

  if (loading && !data) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>
  );

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground text-sm">Fleet performance overview</p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'last_7', 'mtd', 'last_month'].map(t => (
            <Button key={t} variant="ghost" size="sm" onClick={() => setQuickRange(t)}
              className="text-[10px] text-muted-foreground hover:text-foreground h-7 px-2 tracking-wider"
              data-testid={`analytics-range-${t}`}>
              {t === 'all' ? 'ALL' : t === 'last_7' ? '7D' : t === 'mtd' ? 'MTD' : 'LAST MO'}
            </Button>
          ))}
          <Input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
            className="bg-muted border-input text-muted-foreground h-7 text-xs w-32" />
          <Input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
            className="bg-muted border-input text-muted-foreground h-7 text-xs w-32" />
          <Button size="sm" onClick={applyRange} className="bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold h-7 text-xs px-3 rounded-none"
            data-testid="analytics-apply">APPLY</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { icon: Package, label: 'Total Loads', value: data?.total_loads || 0, accent: 'text-foreground' },
          { icon: Package, label: 'Completed', value: data?.completed_loads || 0, accent: 'text-emerald-400' },
          { icon: TrendingUp, label: 'Active', value: data?.active_loads || 0, accent: 'text-cyan-400' },
          { icon: DollarSign, label: 'Revenue', value: `$${(data?.total_revenue || 0).toLocaleString()}`, accent: 'text-brand-accent' },
          { icon: Route, label: 'Total KM', value: `${(data?.total_km || 0).toLocaleString()}`, accent: 'text-foreground' },
          { icon: DollarSign, label: 'Avg $/km', value: `$${data?.avg_rate_per_km || 0}`, accent: 'text-emerald-400' },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border">
            <CardContent className="p-4">
              <s.icon className={`w-4 h-4 ${s.accent} mb-2`} strokeWidth={1.5} />
              <p className="text-xl font-bold text-foreground">{s.value}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Status Distribution */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data?.status_distribution || {}).map(([status, count]) => {
                const pct = data?.total_loads > 0 ? (count / data.total_loads * 100) : 0;
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground capitalize">{status.replace('_', ' ')}</span>
                      <span className="text-foreground font-bold">{count}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-brand-accent h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Monthly Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.monthly_trend || []).length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No monthly data available</p>
            ) : (
              <div className="space-y-2">
                {(data?.monthly_trend || []).map(m => (
                  <div key={m.month} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                    <span className="text-xs text-muted-foreground w-16 font-mono">{m.month}</span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div className="bg-brand-accent h-2 rounded-full" style={{ width: `${data?.total_revenue > 0 ? (m.revenue / data.total_revenue * 100) : 0}%` }} />
                    </div>
                    <span className="text-xs text-brand-accent font-mono w-20 text-right">${m.revenue?.toLocaleString()}</span>
                    <span className="text-[10px] text-muted-foreground w-12 text-right">{m.loads} loads</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Driver Performance */}
      <Card className="bg-card border-border mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground flex items-center gap-2"><Truck className="w-4 h-4" /> Driver Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DRIVER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">LOADS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">KM</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">REVENUE</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">COMMISSION</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">HOURS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">FUEL COSTS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">AVG/LOAD</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.driver_performance || []).map(d => (
                <TableRow key={d.driver_id} className="border-border">
                  <TableCell className="text-xs text-foreground font-medium">{d.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{d.loads}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{d.km?.toLocaleString()} km</TableCell>
                  <TableCell className="text-xs text-brand-accent font-mono">${d.revenue?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-amber-400 font-mono">${d.commission ? d.commission.toLocaleString() : Math.round((d.revenue || 0) * 0.75).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{d.driving_hours ? `${d.driving_hours}h` : '-'}</TableCell>
                  <TableCell className="text-xs text-red-400 font-mono">{d.fuel_costs ? `$${d.fuel_costs.toLocaleString()}` : '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">${d.loads > 0 ? Math.round(d.revenue / d.loads).toLocaleString() : 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Shipper Performance */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground flex items-center gap-2"><Users className="w-4 h-4" /> Shipper Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">SHIPPER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">LOADS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">KM</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">SPEND</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">AVG/LOAD</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.shipper_performance || []).map(s => (
                <TableRow key={s.client_id} className="border-border">
                  <TableCell className="text-xs text-foreground font-medium">{s.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{s.loads}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{s.km?.toLocaleString()} km</TableCell>
                  <TableCell className="text-xs text-purple-400 font-mono">${s.revenue?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">${s.loads > 0 ? Math.round(s.revenue / s.loads).toLocaleString() : 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
